import { Platform } from "react-native"

import i18next, { i18n } from "i18next"

import { SubscriptionResponse } from "@treefort/api-spec"
import { Action } from "@treefort/lib/authenticator"
import { DisplayableError } from "@treefort/lib/displayable-error"
import { getMessageFromAxiosError } from "@treefort/lib/errors"
import { CheckoutError } from "@treefort/lib/errors"

import { refetchUserSubscriptions } from "../../hooks/subscriptions"
import analytics from "../analytics"
import api from "../api"
import authenticator from "../authenticator"
import { getCheckoutCodeValidationMessage } from "../checkout-codes"
import { isCouponCodeValidationError, redeemCouponCode } from "../coupons"
import { getAndroidCheckoutData } from "../in-app-purchases/get-android-checkout-data"
import { requestSubscription } from "../in-app-purchases/request-subscription"
import { logError } from "../logging"
import { openStripeCheckout } from "../stripe"
import {
  canPurchasePlanFromProvider,
  getSubscriptionPlan,
} from "../subscription-plans"
import { getUserSubscriptions } from "../subscriptions"
import { toast } from "../toaster"
import { openWebPaymentCheckout } from "../web-payment"
import { Event, CheckoutSession, checkoutSessionManager } from "./session"

export type { CheckoutSession }

/**
 * Log checkout analytics
 */
checkoutSessionManager.on(
  Event.CheckoutSessionStarted,
  analytics.logCheckoutSessionStart,
)
checkoutSessionManager.on(
  Event.CheckoutSessionEnded,
  ({ session, complete }) => {
    if (complete) {
      analytics.logCheckoutSessionComplete(session)
    }
  },
)

/**
 * Takes a checkout session and attempts to actually checkout (e.g. sign up for
 * a plan, create an account, etc.)
 */
export async function initiateCheckout(
  checkoutSession: CheckoutSession,
  i18n: i18n,
) {
  try {
    await checkoutSessionManager.startSession(checkoutSession)
    const user = authenticator.getUser()
    if (user) {
      await completeCheckout(checkoutSession, i18n)
    } else {
      await authenticator.register()
    }
  } catch (error) {
    void handleCheckoutError(error, i18n)
  }
}

/**
 * Complete a checkout session. This will trigger the payment flow.
 */
async function completeCheckout(
  checkoutSession: CheckoutSession,
  i18n: i18n,
): Promise<void> {
  await confirmCompleteCheckoutAllowed(checkoutSession, i18n)

  // Handle coupons up top since they are the unique use-case. Everything after
  // this point deals with subscription plans.
  if (checkoutSession.type === "coupon") {
    try {
      await redeemCouponCode({ couponCodeId: checkoutSession.couponCode.id })
    } catch (error) {
      // Show an error message to the user if we got a specific validation
      // error back from the redemption endpoint
      if (isCouponCodeValidationError(error)) {
        throw new DisplayableError(
          getCheckoutCodeValidationMessage({
            i18n,
            validationResult: {
              type: "invalidCouponCode",
              data: error.response.data,
            },
          }) || i18n.t("Invalid code."),
          error,
        )
      }

      // Otherwise re-throw the error to be handled upstream
      throw error
    }

    // Complete the checkout session for entitlement coupons here. Completion
    // for subscription plan coupons is handled by
    // handleSubscriptionResponseForCheckout()
    switch (checkoutSession.coupon.type) {
      case "entitlement": {
        checkoutSessionManager.endSession({ complete: true })
        break
      }
      case "subscriptionPlan": {
        await refetchUserSubscriptions()
        break
      }
    }
    return
  }

  switch (checkoutSession.plan.provider) {
    case "appStore": {
      const productId =
        checkoutSession.plan.provider === "appStore"
          ? checkoutSession.plan.productId
          : undefined

      if (!productId) {
        throw new CheckoutError(
          `requestPayment called on ${Platform.OS} with a plan with id ${checkoutSession.plan.id} and no productId`,
        )
      }
      // This promise may not resolve, but it should still be awaited to prevent the
      // calling function from continuing before the purchase completes (on native
      // successful purchases are handled by useInAppPurchases)
      await requestSubscription({
        sku: productId,
      }).catch(() => {
        // Do nothing, errors are handled by the useInAppPurchases error listener
      })
      break
    }
    case "playStore": {
      const productId =
        checkoutSession.plan.provider === "playStore"
          ? checkoutSession.plan.productId
          : undefined
      const offerToken = checkoutSession.plan.offerToken

      if (!productId || !offerToken) {
        throw new CheckoutError(
          `requestPayment called on ${Platform.OS} with an invalid plan with id ${checkoutSession.plan.id}`,
        )
      }

      const { purchaseTokenAndroid, replacementModeAndroid } =
        await getAndroidCheckoutData(checkoutSession)

      // This promise may not resolve, but it should still be awaited to prevent the
      // calling function from continuing before the purchase completes (on native
      // successful purchases are handled by useInAppPurchases)
      await requestSubscription({
        sku: productId,
        purchaseTokenAndroid,
        replacementModeAndroid,
        subscriptionOffers: [{ sku: productId, offerToken }],
      }).catch(() => {
        // Do nothing, errors are handled by the useInAppPurchases error listener
      })
      break
    }
    case "webPayment": {
      await openWebPaymentCheckout({
        subscriptionPlanId: checkoutSession.plan.id,
        promoCode: checkoutSession.promoCode,
        i18n,
      })
      break
    }
    case "stripe": {
      switch (checkoutSession.type) {
        case "paidPlan": {
          await openStripeCheckout({
            subscriptionPlanId: checkoutSession.plan.id,
            promoCode: checkoutSession.promoCode,
          })
          break
        }
        case "paidPlanResubscription": {
          await api.patch("/integrations/stripe/subscription", {
            type: "resubscription",
            subscriptionPlanId: checkoutSession.plan.id,
            promoCode: checkoutSession.promoCode,
          })
          break
        }
        case "paidPlanWithProrationPreview": {
          await api.patch("/integrations/stripe/subscription", {
            type: "proration",
            subscriptionPlanId: checkoutSession.plan.id,
            prorationDate: checkoutSession.prorationDate,
            stripeSubscriptionId: checkoutSession.stripeSubscriptionId,
            promoCode: checkoutSession.promoCode,
          })
          break
        }
      }
      // Refetch the user's subscriptions for Stripe checkout sessions. This
      // will both refresh the app's UI with the user's new access and will end
      // up triggering handleSubscriptionResponseForCheckout which will close
      // out the session and pop a success toast (assuming everything worked
      // correctly).
      await refetchUserSubscriptions()
    }
  }
}

/**
 * Runs some last minute checks to make sure that completing the payment won't
 * put the user in an invalid subscripion state.
 */
async function confirmCompleteCheckoutAllowed(
  checkoutSession: CheckoutSession,
  i18n: i18n,
) {
  if (checkoutSession.type === "coupon") {
    return true
  }

  if (!(await canPurchasePlanFromProvider(checkoutSession.plan.provider))) {
    throw new CheckoutError(
      `User attempted to subscribe to plan #${checkoutSession.plan.id} (${checkoutSession.plan.provider}) which is not available on platform ${Platform.OS}`,
    )
  }

  const { subscriptions, currentSubscriptionId, redundantSubscriptionIds } =
    await getUserSubscriptions()

  // If the user is in a bad state then stop now.
  if (redundantSubscriptionIds.length) {
    throw new DisplayableError(
      i18n.t("You are already subscribed."),
      new CheckoutError(
        `User attempted payment while subscribed to multiple plans`,
      ),
    )
  }

  // Extract the user's current subscription and the plan that goes with it
  const subscription = subscriptions.find(
    (sub) => sub.id === currentSubscriptionId,
  )
  const currentPlan = subscription
    ? await getSubscriptionPlan(subscription.subscriptionPlanId)
    : undefined

  // If the user has an uncanceled paid plan from another provider then in most
  // cases we ask them to manually cancel it first. On a technical level we
  // might be able to cancel the plan automatically, but we can't prorate
  // switches between providers. The idea is that if we force the user to
  // manually cancel the old plan we will reduce the chance that they excpect
  // the new plan to be prorated when they sign up again. This is pretty
  // subjective - implement automatic cancellation and tear down this fence if
  // you have good reason to.
  if (
    subscription &&
    currentPlan &&
    currentPlan.provider !== checkoutSession.plan.provider &&
    !subscription.canceledAt &&
    !subscription.deactivatedAt &&
    // Switching away from a group membership doesn't involve proration so we
    // allow the user to do this seamlessly.
    currentPlan.provider !== "groupMembership" &&
    // When a user switches to a Web Payment plan from a Stripe plan we
    // automatically set their Stripe plan to cancel at period end. It's up to
    // the tenant implementing the Web Payment integration to handle proration.
    !(
      currentPlan.provider === "stripe" &&
      checkoutSession.plan.provider === "webPayment"
    )
  ) {
    throw new DisplayableError(
      i18n.t(
        "You are already subscribed. Please cancel your current subscription first.",
      ),
      new CheckoutError(
        `User with subscription id ${subscription.id} attempted payment on one platform, but they have an uncanceled subscription on another platform`,
      ),
    )
  }

  // We can't use the request payment flow to switch Stripe plans (that must be
  // done via the Stripe billing portal or our built-in plan switching logic).
  if (
    subscription &&
    checkoutSession.plan.provider === "stripe" &&
    currentPlan?.provider === "stripe" &&
    !subscription.canceledAt &&
    !subscription.deactivatedAt &&
    checkoutSession.type !== "paidPlanWithProrationPreview"
  ) {
    throw new DisplayableError(
      i18n.t("You are already subscribed."),
      new CheckoutError(
        `User with subscription id ${subscription.id} attempted to open the Stripe checkout portal but they already have an uncanceled Stripe subscription`,
      ),
    )
  }
}

/**
 * Takes a subscription response object from the api, checks if it matches the
 * current checkout session, if any, and ends the checkout session with a
 * success message if it does
 */
export async function handleSubscriptionResponseForCheckout({
  subscribed,
  willRenewWithSubscriptionPlanId,
}: SubscriptionResponse) {
  const checkoutSession = await checkoutSessionManager.getSession()
  switch (checkoutSession?.type) {
    case "paidPlan":
    case "paidPlanResubscription":
    case "paidPlanWithProrationPreview":
      if (
        (subscribed === checkoutSession.plan.id &&
          (willRenewWithSubscriptionPlanId === subscribed ||
            !willRenewWithSubscriptionPlanId)) ||
        willRenewWithSubscriptionPlanId === checkoutSession.plan.id
      ) {
        handleSubscriptionActivated()
      }
      break
    case "coupon": {
      const subscriptionPlanId =
        checkoutSession.coupon.type === "subscriptionPlan"
          ? checkoutSession.coupon.redeems.subscriptionPlanId
          : null
      if (
        subscriptionPlanId &&
        (subscriptionPlanId === subscribed ||
          (Array.isArray(subscribed) &&
            subscribed.includes(subscriptionPlanId)))
      ) {
        handleSubscriptionActivated()
      }
      break
    }
  }
}

/**
 * Completes any outstanding checkout session after the user logs in or
 * registers
 */
export async function handleCompletedAuthActionForCheckout(action: Action) {
  const checkoutSession = await checkoutSessionManager.getSession()
  const signedIn = action === "login" || action === "register"
  if (signedIn && checkoutSession) {
    try {
      await completeCheckout(checkoutSession, i18next)
    } catch (error) {
      void handleCheckoutError(error, i18next)
    }
  }
}

async function handleCheckoutError(error: unknown, i18n: i18n) {
  await checkoutSessionManager.endSession({ complete: false })
  await refetchUserSubscriptions()
  const message = getMessageFromAxiosError(error)
  logError(
    error instanceof DisplayableError
      ? error
      : new DisplayableError(
          message && message.toLowerCase().startsWith("invalid promo code")
            ? i18n.t("Invalid code.")
            : i18n.t("An error occurred. Please try again."),
          error,
        ),
  )
}

function handleSubscriptionActivated() {
  checkoutSessionManager.endSession({ complete: true })
  toast.success(i18next.t("Your subscription has been activated. Thank you!"))
}
